import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { MovieItem } from '@/mocks/movies';

interface HeroCarouselProps {
  items: MovieItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % items.length);
  }, [current, items.length, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const item = items[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={item.poster}
          alt={item.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background-50"></div>
      </div>

      <div className="absolute inset-0 flex items-end pb-16 md:pb-20">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl animate-fade-in-up" key={current}>
            <div className="flex items-center gap-3 mb-3">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500/90 text-white whitespace-nowrap">
                {item.categoryLabel}
              </span>
              <span className="px-2 py-1 rounded text-xs font-semibold bg-accent-500/90 text-white whitespace-nowrap">
                {item.rating} 分
              </span>
              {item.isNew && (
                <span className="px-2 py-1 rounded text-xs font-medium bg-secondary-500/80 text-white whitespace-nowrap">
                  NEW
                </span>
              )}
            </div>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold font-heading mb-3 text-white drop-shadow-lg">
              {item.title}
            </h2>
            <p className="text-sm md:text-base text-white/80 mb-4 line-clamp-2 max-w-xl">
              {item.description}
            </p>
            <div className="flex items-center gap-3">
              <Link
                to={`/article/${item.id}`}
                className="px-6 py-2.5 rounded-full text-sm font-semibold bg-primary-500 hover:bg-primary-600 text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-fill mr-1.5"></i>立即播放
              </Link>
              <Link
                to={`/article/${item.id}`}
                className="px-6 py-2.5 rounded-full text-sm font-medium border border-white/30 text-white hover:bg-white/10 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                查看详情
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 right-4 md:right-8 flex items-center gap-2">
        {items.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              idx === current ? 'w-6 bg-primary-500' : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Slide ${idx + 1}`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + items.length) % items.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>
    </section>
  );
}